
import React, { useState } from 'react';
import { type Gift, type Brand, type ProductSilo } from '@/utils/types';
import InventoryActions from './inventory/InventoryActions';
import InventoryHeader from './inventory/InventoryHeader';
import MarketQRCodeView from './market/MarketQRCodeView';
import BrandTransactions from './BrandTransactions';
import TransactionActionDialog from './transactions/TransactionActionDialog';
import { useInventoryActions } from '@/hooks/inventory';

interface MicroGiftInventoryProps {
  gifts: Gift[];
  brands: Brand[];
  onBack?: () => void;
  onRedeem?: (gift: Gift) => void;
}

const MicroGiftInventory: React.FC<MicroGiftInventoryProps> = ({ 
  gifts: initialGifts,
  brands,
  onBack,
  onRedeem
}) => {
  const {
    activeTab,
    selectedGift,
    targetGift,
    targetEmail,
    isProcessing,
    gifts,
    currentBrand,
    viewingTransactions,
    showQRCode,
    handleTabChange,
    handleGiftSelect,
    setTargetGift,
    setTargetEmail,
    viewBrandTransactions,
    handleBackFromTransactions,
    handleShowQRCode,
    handleCancelQRCode,
    handleMarkGiftAsUsed,
    handleTransactionAction,
    handleMarketAction
  } = useInventoryActions(initialGifts);
  
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  
  const handleSelectTransaction = (gift: Gift) => {
    handleGiftSelect(gift);
    setIsActionDialogOpen(true);
  };
  
  const handleCloseActionDialog = () => {
    setIsActionDialogOpen(false);
  };
  
  const availableGifts = gifts.filter(gift => !gift.isRedeemed);
  const totalBalance = availableGifts.reduce((sum, gift) => sum + gift.amount, 0);

  // Filter gifts by current brand when viewing a specific brand
  const filteredGifts = currentBrand 
    ? availableGifts.filter(gift => gift.brandId === currentBrand.id)
    : availableGifts;
  
  if (showQRCode && selectedGift) {
    return (
      <MarketQRCodeView
        selectedGift={selectedGift}
        brands={brands}
        onMarkAsUsed={handleMarkGiftAsUsed}
        onBack={handleCancelQRCode}
      />
    );
  }
  
  if (viewingTransactions && currentBrand) {
    const brandGifts = availableGifts.filter(gift => gift.brandId === currentBrand.id);
    
    return (
      <BrandTransactions 
        brand={currentBrand}
        gifts={brandGifts}
        onBack={handleBackFromTransactions}
        onAction={handleTransactionAction}
      />
    );
  }
  
  return (
    <div className="space-y-6">
      <InventoryHeader totalBalance={totalBalance} />
      
      <div>
        <InventoryActions 
          selectedGift={selectedGift}
          gifts={filteredGifts}
          brands={brands}
          onSelectTransaction={handleSelectTransaction}
          onBack={onBack}
        />
      </div>

      <TransactionActionDialog
        isOpen={isActionDialogOpen}
        selectedGift={selectedGift}
        targetGift={targetGift}
        targetEmail={targetEmail}
        activeTab={activeTab}
        brands={brands}
        isProcessing={isProcessing}
        onClose={handleCloseActionDialog}
        onTabChange={handleTabChange}
        onTargetGiftChange={setTargetGift}
        onTargetEmailChange={setTargetEmail}
        onAction={handleMarketAction}
        onShowQRCode={handleShowQRCode}
      />
    </div>
  );
};

export default MicroGiftInventory;
